import * as React from 'react';
import { ContractBalancesViewModel, ContractDetailsViewModel } from '@models/Contract';
import { HostedCheckout } from 'src/payment/_components/gateway/hosted-checkout-container';
import { PaymentMethods } from '@models/HostedCheckoutTransactionViewModel';
import Constants from '@constants/Constants';
import { PaymentTerms, PaymentType, PaymentPurpose, ContractCheckPaymentViewModel } from '@models/Payment';
import { CheckboxInline } from '@common/components';
import { Control, LocalForm, Errors } from 'react-redux-form';
import { hasValue, getDefaultErrorMessages, hasDateValue } from '@common/validators';
import { OptionsViewModel } from '@models/common.models';
import ReactDatePicker from 'react-datepicker';
import { dateParser } from '@common/dateParser';
import * as moment from 'moment-timezone';

interface ContractBalancePaymentComponentProperties {
  readonly contractDetailsVM: ContractDetailsViewModel;
  readonly selectedBalances: Array<ContractBalancesViewModel>;
  readonly paymentType: PaymentType;
  readonly paymentSources: Array<OptionsViewModel>;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly setPaymentType: (paymentType: PaymentType) => void;
  readonly startCreditCardPayment: () => void;
  readonly saveCheckPayment: (checkPayment: ContractCheckPaymentViewModel) => void;
}

export const ContractBalancePaymentComponent = (props: ContractBalancePaymentComponentProperties) => {
  const balances = props.selectedBalances.map((balance: ContractBalancesViewModel, index) => {
    return (
      <div className="col-md-12" key={index}>
        {balance.balanceType.value} #{balance.entityId} - ${balance.amountDue.toFixed(2)}
      </div>
    );
  });

  const totalPrice = props.selectedBalances.reduce((sum, current) => sum + current.amountDue, 0);

  const renderPaymentSources = () => {
    if (!props.paymentSources) {
      return <>No Payment Sources</>;
    }

    const options: Array<JSX.Element> = props.paymentSources.map(ps => {
      return (
        <option key={ps.id} value={ps.id}>
          {ps.value}
        </option>
      );
    });
    options.unshift(
      <option key={-1} value={-1}>
        Select one...
      </option>
    );

    return <>{options}</>;
  };

  return (
    <>
      <div className="row">
        <h2>Items Selected for Payment</h2>
      </div>
      <div className="row mt-15">{balances}</div>
      <div className="row mt-25">
        <div className="col-md-12">Total: ${totalPrice.toFixed(2)}</div>
      </div>
      <div className="row mt-25">
        <HostedCheckout
          contractId={props.contractDetailsVM ? props.contractDetailsVM.id : undefined}
          getHiddenFormButton={props.buttonRef}
          paymentMethod={PaymentMethods.CreditCard}
          installmentsFee={Constants.Payments.INSTALLMENT_SERVICE_FEE}
          paymentAmountInitial={totalPrice}
          paymentAmountBalance={0}
          paymentTerms={PaymentTerms.OneInstallment}
          paymentTypeInitial={PaymentType.CreditCard}
          paymentTypeBalance={PaymentType.CreditCard}
          paymentPurpose={PaymentPurpose.Multiple}
          remainingBalance={0}
          remainingBalanceDueString="0"
          multiplePayments={props.selectedBalances}
        />
        <div className="col-md-12">
          <CheckboxInline>
            <Control
              checked={props.paymentType === PaymentType.CreditCard}
              id="creditCardPayment"
              model="balancesPayment"
              name="balancesPayment"
              onChange={e => props.setPaymentType(PaymentType.CreditCard)}
              value={PaymentType.CreditCard}
              type="radio"
            />
            <label htmlFor="creditCardPayment">Pay by Credit Card</label>
          </CheckboxInline>
        </div>
        {props.paymentType === PaymentType.CreditCard && (
          <div className="col-md-offset-2 col-md-10">
            <LocalForm
              className="form-horizontal"
              hideNativeErrors={true}
              onSubmit={form => props.startCreditCardPayment()}
            >
              <h3>Please click Proceed to First Data to enter credit card information and complete payment.</h3>
              <div className="row text-right">
                <div className="col-xs-12">
                  <button className="btn btn-primary" type="submit">
                    Proceed to First Data
                  </button>
                </div>
              </div>
            </LocalForm>
          </div>
        )}
      </div>
      <div className="row mt-25">
        <div className="col-md-12">
          <CheckboxInline>
            <Control
              checked={props.paymentType === PaymentType.Check}
              id="checkPayment"
              model="balancesPayment"
              name="balancesPayment"
              onChange={e => props.setPaymentType(PaymentType.Check)}
              value={PaymentType.Check}
              type="radio"
            />
            <label htmlFor="checkPayment">Pay by Check</label>
          </CheckboxInline>
        </div>
        {props.paymentType === PaymentType.Check && (
          <div className="col-md-offset-2 col-md-10">
            <LocalForm
              className="form-horizontal"
              hideNativeErrors={true}
              onSubmit={form => props.saveCheckPayment(form)}
            >
              <h3>Please enter check information.</h3>
              <div className="row">
                <div className="form-control col-md-4">
                  <label htmlFor=".paymentSource.id">Payment Source</label>
                  <Control.select id=".paymentSource.id" model=".paymentSource.id" validators={{ required: hasValue }}>
                    {renderPaymentSources()}
                  </Control.select>
                  <Errors
                    className="error"
                    model=".paymentSource.id"
                    show={{ touched: true, focus: false }}
                    messages={getDefaultErrorMessages()}
                  />
                </div>
                <div className="form-control col-md-4">
                  <label htmlFor=".checkDate">Check Date</label>
                  <Control
                    model=".checkDate"
                    validators={{ hasDateValue: hasDateValue }}
                    // tslint:disable-next-line:no-shadowed-variable
                    component={(props: any) => {
                      let val;
                      if (props.value && !moment.isMoment(props.value)) {
                        val = dateParser.parse(props.value);
                      } else {
                        // already a moment
                        val = props.value;
                      }
                      return (
                        <ReactDatePicker
                          {...props}
                          value={val ? val.format('MM/DD/YYYY') : ''}
                          selected={val || null}
                          maxDate={moment()}
                        />
                      );
                    }}
                  />
                  <Errors
                    className="error"
                    model=".checkDate"
                    show={{ touched: true, focus: false }}
                    messages={{ ...getDefaultErrorMessages(), validDate: 'Invalid Date' }}
                  />
                </div>
                <div className="form-control col-md-4">
                  <label htmlFor=".checkNumber">Check Number</label>
                  <Control.text
                    id=".checkNumber"
                    model=".checkNumber"
                    maxLength={12}
                    validators={{ required: hasValue }}
                  />
                  <Errors
                    className="error"
                    messages={getDefaultErrorMessages()}
                    model=".checkNumber"
                    show={{ touched: true, focus: false }}
                  />
                </div>
              </div>
              <div className="row text-right">
                <div className="col-xs-12">
                  <button className="btn btn-primary" type="submit">
                    Submit
                  </button>
                </div>
              </div>
            </LocalForm>
          </div>
        )}
      </div>
    </>
  );
};



// WEBPACK FOOTER //
// ./src/contract/_components/contract-balances/_components/contract-balance-payment.component.tsx